import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Bot, FolderGit2, Keyboard, List, Plus, Save, Trash2 } from 'lucide-react';
import { aiDispatchReposApi } from '../../api/aiDispatchRepos';
import { productLinesApi } from '../../api/productLines';
import { ticketsApi } from '../../api/tickets';
import { useCompany } from '../../contexts/CompanyContext';
import EmptyState from '../EmptyState';
import { useConfirm } from '../ConfirmDialog';

// Mirrors the server's normalizeAgentName rules (Redis key segment).
const AGENT_NAME_RE = /^[a-z0-9][a-z0-9._-]{0,31}$/;

interface AgentDraft {
  defaultRepo: string;
  byLine: Record<string, string>; // productLineId → path ('' / absent = unmapped)
}

const OTHER = '__other__';

/**
 * One folder value: a dropdown of the folders the agent reported from its
 * machine (plus "Type a path…" for anything else), falling back to a plain
 * text field for agents that haven't reported an inventory yet.
 */
function PathField({ value, onChange, candidates, noneLabel, placeholder }: {
  value: string;
  onChange: (v: string) => void;
  candidates: string[];
  noneLabel: string;
  placeholder: string;
}) {
  const [typing, setTyping] = useState(false);

  if (candidates.length === 0 || typing) {
    return (
      <div className="flex gap-1 flex-1 min-w-0">
        <input
          className="input flex-1 font-mono text-sm min-w-0"
          placeholder={placeholder}
          value={value}
          onChange={e => onChange(e.target.value)}
        />
        {candidates.length > 0 && (
          <button
            className="btn btn-ghost text-sm shrink-0"
            title="Pick from this machine's folders instead"
            onClick={() => setTyping(false)}
          >
            <List className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  const known = value === '' || candidates.includes(value);
  return (
    <select
      className="select flex-1 font-mono text-sm min-w-0"
      value={value}
      onChange={e => {
        if (e.target.value === OTHER) setTyping(true);
        else onChange(e.target.value);
      }}
    >
      <option value="">{noneLabel}</option>
      {candidates.map(c => <option key={c} value={c}>{c}</option>)}
      {!known && <option value={value}>{value}</option>}
      <option value={OTHER}>Type a path…</option>
    </select>
  );
}

export default function ClaudeDispatchTab() {
  const { currentCompany } = useCompany();
  const companyId = currentCompany?.id;
  const qc = useQueryClient();
  const confirm = useConfirm();

  const [drafts, setDrafts] = useState<Record<string, AgentDraft>>({});
  const [newAgentName, setNewAgentName] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['ai-dispatch-repos', companyId],
    queryFn: () => aiDispatchReposApi.list(),
    enabled: !!companyId,
  });
  const mappings = data?.mappings;
  const agentRepos = data?.agentRepos ?? {};

  const { data: productLines = [] } = useQuery({
    queryKey: ['product-lines', companyId],
    queryFn: () => productLinesApi.list(),
    enabled: !!companyId,
  });

  // Presence refreshes on an interval so the online dots track reality while
  // the tab is open (agents mark themselves seen on every poll).
  const { data: presence } = useQuery({
    queryKey: ['ai-dispatch-agents'],
    queryFn: () => ticketsApi.aiDispatchAgents(),
    enabled: !!companyId,
    refetchInterval: 30000,
  });
  const onlineAgents = presence?.agents ?? [];

  // Seed editable drafts once per agent (saved agents + currently-online
  // ones). Only missing agents are added, so a background refetch never
  // clobbers in-progress edits — the no-cache policy refetches aggressively.
  useEffect(() => {
    if (!mappings) return;
    setDrafts(prev => {
      let changed = false;
      const next = { ...prev };
      const known = new Set([...mappings.map(m => m.agentName), ...onlineAgents]);
      for (const agent of known) {
        if (next[agent]) continue;
        const rows = mappings.filter(m => m.agentName === agent);
        const byLine: Record<string, string> = {};
        for (const r of rows) if (r.productLineId) byLine[r.productLineId] = r.repoPath;
        next[agent] = {
          defaultRepo: rows.find(r => r.productLineId === null)?.repoPath ?? '',
          byLine,
        };
        changed = true;
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappings, onlineAgents.join(',')]);

  const saveMutation = useMutation({
    mutationFn: ({ agent, data }: {
      agent: string;
      data: { defaultRepo: string | null; repos: { productLineId: string; repoPath: string }[] };
    }) => aiDispatchReposApi.save(agent, data),
    onSuccess: (_rows, vars) => {
      qc.invalidateQueries({ queryKey: ['ai-dispatch-repos'] });
      toast.success(`Repo mappings saved for ${vars.agent}`);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e.message),
  });

  const removeMutation = useMutation({
    mutationFn: (agent: string) => aiDispatchReposApi.remove(agent),
    onSuccess: (_res, agent) => {
      setDrafts(prev => {
        const next = { ...prev };
        delete next[agent];
        return next;
      });
      qc.invalidateQueries({ queryKey: ['ai-dispatch-repos'] });
      toast.success(`Mappings removed for ${agent}`);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e.message),
  });

  const setAgentDefault = (agent: string, path: string) =>
    setDrafts(prev => ({ ...prev, [agent]: { ...prev[agent], defaultRepo: path } }));

  const setAgentLine = (agent: string, productLineId: string, path: string) =>
    setDrafts(prev => ({
      ...prev,
      [agent]: { ...prev[agent], byLine: { ...prev[agent].byLine, [productLineId]: path } },
    }));

  const addAgent = () => {
    const name = newAgentName.trim().toLowerCase();
    if (!AGENT_NAME_RE.test(name)) {
      toast.error('Agent names are lowercase letters/digits/._- (max 32), e.g. "gary"');
      return;
    }
    if (drafts[name]) {
      toast.error(`Agent "${name}" is already listed`);
      return;
    }
    setDrafts(prev => ({ ...prev, [name]: { defaultRepo: '', byLine: {} } }));
    setNewAgentName('');
  };

  const saveAgent = (agent: string) => {
    const draft = drafts[agent];
    saveMutation.mutate({
      agent,
      data: {
        defaultRepo: draft.defaultRepo.trim() || null,
        repos: productLines
          .map(pl => ({ productLineId: pl.id, repoPath: (draft.byLine[pl.id] ?? '').trim() }))
          .filter(r => r.repoPath),
      },
    });
  };

  const removeAgent = async (agent: string) => {
    const hasSaved = (mappings ?? []).some(m => m.agentName === agent);
    if (!hasSaved) {
      // Nothing on the server yet — just drop the local card.
      setDrafts(prev => {
        const next = { ...prev };
        delete next[agent];
        return next;
      });
      return;
    }
    if (await confirm({
      title: 'Remove agent mappings',
      message: `Remove all repo mappings for "${agent}"? Its dispatches will fall back to the local config on that machine.`,
      confirmLabel: 'Remove',
      danger: true,
    })) {
      removeMutation.mutate(agent);
    }
  };

  const agentNames = Object.keys(drafts).sort();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="section-title">Claude Dispatch</h2>
        <p className="text-sm text-muted mt-1">
          Where <strong>Send to Claude</strong> opens each ticket: pick the repo folder on each
          developer's machine per product line. Folder lists come straight from the agent's
          machine (it reports its project folders when it runs). Changes apply to the next
          dispatched ticket — no agent restart needed. Product lines left unmapped use the
          default repo.
        </p>
      </div>

      {isLoading ? (
        <div className="card p-6 text-center text-muted">Loading…</div>
      ) : agentNames.length === 0 ? (
        <div className="card p-0">
          <EmptyState icon={<Bot className="w-6 h-6" />}>
            No dispatch agents yet. Start a claude-dispatch agent (it appears here automatically),
            or add one by name below to set up its repos ahead of time.
          </EmptyState>
        </div>
      ) : (
        agentNames.map(agent => {
          const draft = drafts[agent];
          const online = onlineAgents.includes(agent);
          const candidates = agentRepos[agent] ?? [];
          return (
            <div key={agent} className="card p-4 space-y-4">
              <div className="flex items-center gap-2">
                <Bot className="w-4 h-4 text-primary shrink-0" />
                <span className="font-medium text-fg font-mono">{agent}</span>
                <span className={`badge ${online ? 'text-ok' : 'text-muted'}`}>
                  {online ? 'Online' : 'Offline'}
                </span>
                <div className="flex-1" />
                <button
                  className="btn btn-ghost text-sm text-err"
                  onClick={() => removeAgent(agent)}
                  disabled={removeMutation.isPending}
                >
                  <Trash2 className="w-4 h-4" /> Remove
                </button>
                <button
                  className="btn btn-primary text-sm"
                  onClick={() => saveAgent(agent)}
                  disabled={saveMutation.isPending}
                >
                  <Save className="w-4 h-4" /> Save
                </button>
              </div>

              {candidates.length === 0 && (
                <p className="text-xs text-muted flex items-center gap-1">
                  <Keyboard className="w-3.5 h-3.5 shrink-0" />
                  This agent hasn't reported its folders yet (it does so each time it starts, once
                  it's on the 2026-08-31 agent update) — paths are typed by hand until then.
                </p>
              )}

              <div className="grid gap-2" style={{ gridTemplateColumns: 'minmax(10rem, 14rem) 1fr' }}>
                <div className="flex items-center text-sm font-medium text-fg">
                  Default repo
                </div>
                <PathField
                  value={draft.defaultRepo}
                  onChange={v => setAgentDefault(agent, v)}
                  candidates={candidates}
                  noneLabel="— no default —"
                  placeholder={'C:\\projects\\ctTicketing'}
                />

                {productLines.map(pl => (
                  <div key={pl.id} className="contents">
                    <div className="flex items-center gap-2 text-sm text-fg min-w-0">
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: pl.color }}
                      />
                      <span className="truncate" title={pl.name}>{pl.name}</span>
                    </div>
                    <PathField
                      value={draft.byLine[pl.id] ?? ''}
                      onChange={v => setAgentLine(agent, pl.id, v)}
                      candidates={candidates}
                      noneLabel="— use default repo —"
                      placeholder="Leave empty to use the default repo"
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}

      {/* Add an agent that hasn't polled yet (e.g. configure before first run) */}
      <div className="card p-4">
        <label className="label">Add an agent by name</label>
        <div className="flex gap-2">
          <input
            className="input flex-1"
            placeholder='Agent name from its config, e.g. "gary"'
            value={newAgentName}
            onChange={e => setNewAgentName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addAgent(); }}
          />
          <button className="btn btn-secondary" onClick={addAgent} disabled={!newAgentName.trim()}>
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
        <p className="text-xs text-muted mt-1">
          Agents that are running show up here automatically; adding by name just lets you set up
          the repos before the agent first connects.
        </p>
      </div>

      <div className="text-xs text-muted space-y-1">
        <p className="flex items-center gap-1">
          <FolderGit2 className="w-3.5 h-3.5" />
          Mappings here override the agent's local <code className="font-mono">claude-dispatch.config.json</code>.
          Agents older than 2026-08-31 ignore them and keep using their local file until updated.
        </p>
      </div>
    </div>
  );
}
